// Shared video-generation payload builder.
//
// Both the single-shot Pre-Production modal and the bulk "Generate All
// Scene/Project Videos" flow MUST produce identical request payloads for the
// same shot. This module centralises the logic so the bulk path becomes
// "loop over shots and call individual flow N times" — exactly what the
// frontend product spec calls for.

import { getAllVideoModels } from './aiService';
import { uploadDataUriToBackend } from './backendService';
//
// Inputs:
//   shot             - Shot object (with .preProduction, .dialogues, .textOverlays, etc.)
//   projectData      - { characters, aspectRatio, ... }
//   characters       - characters relevant to this shot (caller may pass [] to use projectData)
//
// Output:
//   {
//     finalPrompt,     // string — same shape as VideoPreProductionModal.constructFinalPrompt
//     dialoguesData,   // snake_case array — same shape as modal's structured payload
//     cinematicActions,// snake_case array — same shape as modal's payload
//     duration,        // seconds (matches modal's computeContentDuration)
//     hasEndFrame,
//     startFrameImage, endFrameImage,
//     aspectRatio
//   }

const parseSeconds = (v) => {
  if (v === undefined || v === null) return null;
  if (typeof v === 'number') return v;
  const m = String(v).match(/[\d.]+/);
  return m ? parseFloat(m[0]) : null;
};

/**
 * Resolve the user-selected video model to a canonical backend model id.
 * Reads `localStorage.selectedAIModels[<capability>]` and matches it against
 * the live `getAllVideoModels()` list (exact then partial match).
 *
 * Returns: { id, name, capabilityKey, hasEndFrame } or null on hard failure.
 *
 * IMPORTANT: This is intentionally the SAME logic that Storyboard and
 * VideoPreProductionModal use. The result is stamped onto
 * `shot.preProduction.selectedVideoModel` immediately after pre-production
 * returns, so subsequent bulk/modal paths can skip the network round-trip
 * and fallback-hunting behaviour when the resolver races `preloadAvailableModels`.
 */
export const resolveSelectedVideoModel = async (hasEndFrame, isReferencesToVideo = false) => {
  let selectedMap = {};
  try { selectedMap = JSON.parse(localStorage.getItem('selectedAIModels') || '{}'); }
  catch { selectedMap = {}; }

  // Panel-based shots use the `references-to-video` capability. The model
  // pool, default-key lookup, and fallback ladder are all distinct from the
  // legacy image-to-video / start-end-frame pools — see REFERENCES_TO_VIDEO_SPEC.md.
  //
  // IMPORTANT: We do NOT cross-capability fall back here. A start-end-frame
  // or image-to-video model key cannot consume `reference_images[]` on the
  // backend; if we accidentally returned such a key the request would be
  // routed to the wrong renderer (e.g. backend pipes the panels through a
  // legacy 2-frame model and ignores all but the first/last). The selection
  // is restricted to the refs-to-video aliases only — if none are saved,
  // the live catalog pool is the source of truth.
  const capabilityKey = isReferencesToVideo
    ? 'references to video'
    : (hasEndFrame ? 'start-end-frame videos' : 'Image to video');
  let rawKey;
  if (isReferencesToVideo) {
    // Feb 13 2026 — Case-insensitive lookup. Admin catalogs sometimes
    // save the capability key as `'References to video'` (capital R with
    // spaces). Iterate every localStorage key and match by lowercased
    // alias so the user's AI Settings pick is honoured regardless of the
    // exact capital-letter form the admin uses.
    const REFS_ALIASES = new Set([
      'references to video',
      'references-to-video',
      'references_to_video',
      'referencestovideo',
      'reference to video',
      'reference-to-video',
      'reference_to_video',
      'referencetovideo',
    ]);
    for (const [k, v] of Object.entries(selectedMap || {})) {
      if (REFS_ALIASES.has(String(k).toLowerCase().trim())) {
        rawKey = v;
        break;
      }
    }
    // NOTE: Do NOT fall back to `start-end-frame videos` or `Image to video`
    // here — those keys belong to incompatible capabilities and would be
    // matched against the (concatenated) pool below, returning a model
    // that cannot consume reference_images[]. See bug report Feb 2026.
  } else if (hasEndFrame) {
    rawKey = selectedMap['start-end-frame videos']
      || selectedMap['start-end-frame video']
      || selectedMap['startEndFrame']
      || selectedMap['start_end_frame_videos']
      || selectedMap['Image to video'];
  } else {
    rawKey = selectedMap['Image to video']
      || selectedMap['image-to-video']
      || selectedMap['imageToVideo']
      || selectedMap['image_to_video'];
  }

  try {
    const all = await getAllVideoModels();
    let pool;
    if (isReferencesToVideo) {
      // STRICT: refs-to-video shots can ONLY be served by refs-to-video
      // models. Cross-capability fallback was removed (Feb 2026) — sending
      // a start-end-frame or image-to-video model_key alongside
      // reference_images[] causes the backend to silently route to the
      // wrong renderer and ignore the panel ordering.
      pool = [...(all.referencesToVideo || [])];
    } else if (hasEndFrame) {
      pool = [...(all.startEndFrame || []), ...(all.imageToVideo || [])];
    } else {
      pool = [...(all.imageToVideo || []), ...(all.startEndFrame || [])];
    }
    const transformed = pool.map(m => ({
      id: m.key || m.id || m.name,
      name: m.name || m.key || m.id
    }));
    if (rawKey) {
      const needle = String(rawKey).toLowerCase().trim();
      const match = transformed.find(m => {
        const id = (m.id || '').toLowerCase().trim();
        const name = (m.name || '').toLowerCase().trim();
        return id === needle || name === needle ||
          id.includes(needle) || needle.includes(id) ||
          name.includes(needle) || needle.includes(name);
      });
      if (match) return { id: match.id, name: match.name, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
      // No catalog match BUT the user explicitly saved this model key
      // (e.g. via Tier Selection or the AI Toolkit). Honour their pick
      // verbatim — the backend will look up the key in its own registry,
      // which often contains tier-specific models that the public model
      // catalog doesn't expose (the catalog's `models[]` array is a subset
      // of `tiers.*[]`). See bug Feb 28, 2026: Economy tier wrote
      // `pixverse-ref2video` to localStorage, but the catalog only listed
      // `seedance2-ref2video` in `models[]` → resolver silently returned
      // the catalog's first entry (= Premium), ignoring the tier pick.
      return { id: rawKey, name: rawKey, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
    }
    // No saved selection AND no match — last resort is the catalog default.
    // This is the only path where we accept whatever the catalog lists first.
    if (transformed.length > 0) {
      return { id: transformed[0].id, name: transformed[0].name, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
    }
  } catch { /* swallow — fall through */ }

  if (rawKey) return { id: rawKey, name: rawKey, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
  return null;
};

/**
 * Stamp both `selectedVideoModelImageToVideo` and `selectedVideoModelStartEndFrame`
 * onto each shot's `preProduction`. We stamp BOTH variants because pre-production
 * typically runs BEFORE frame generation — at that moment we don't yet know
 * whether the shot will have one or two frames. Storing both lets the resolver
 * pick the correct one at video-generation time based on the shot's current
 * frame state, instead of relying on a stale `hasEndFrame` snapshot from
 * pre-production time.
 *
 * For backwards compatibility we also keep `selectedVideoModel` populated with
 * the start-end-frame variant when it exists, otherwise the image-to-video one.
 *
 * Pure: returns a new shots array; does not mutate input.
 */
export const stampSelectedVideoModelOnShots = async (shots) => {
  if (!Array.isArray(shots) || shots.length === 0) return shots;
  // Resolve all three variants once for the whole batch (network calls coalesce
  // through the cached `getAllVideoModels()`).
  const [imgToVideo, startEnd, refsToVideo] = await Promise.all([
    resolveSelectedVideoModel(false).catch(() => null),
    resolveSelectedVideoModel(true).catch(() => null),
    resolveSelectedVideoModel(false, true).catch(() => null),
  ]);
  return shots.map((sh) => ({
    ...sh,
    preProduction: {
      ...(sh.preProduction || {}),
      selectedVideoModelImageToVideo: imgToVideo,
      selectedVideoModelStartEndFrame: startEnd,
      selectedVideoModelReferencesToVideo: refsToVideo,
      // Keep legacy field too — prefer refs-to-video if shot uses panels,
      // else start-end-frame, else image-to-video.
      selectedVideoModel: (Array.isArray(sh?.panels) && sh.panels.some(p => !!p?.image))
        ? (refsToVideo || startEnd || imgToVideo || null)
        : (startEnd || imgToVideo || null)
    }
  }));
};


const formatSec = (n) => `${Number(n || 0).toFixed(1)}s`;

// Estimate speech duration — handles non-Latin scripts (Kannada, Hindi, etc.)
// IDENTICAL to VideoPreProductionModal.estimateSpeechDuration.
export const estimateSpeechDuration = (text) => {
  if (!text || !text.trim()) return 0;
  const wordCount = text.trim().split(/\s+/).length;
  const charCount = text.trim().length;
  const wordEstimate = Math.ceil(wordCount / 2.5);
  const charEstimate = Math.ceil(charCount * 0.08);
  return Math.max(2, Math.max(wordEstimate, charEstimate) + 1);
};

/**
 * Build the dialogues map keyed by characterId — same shape modal stores in
 * its `dialogues` state. Honours admin-supplied timing from
 * shot.preProduction.dialogues; falls back to sequential estimation when
 * admin timing is unavailable for a character.
 */
export const buildDialoguesMap = (shot, projectCharacters, shotCharacters) => {
  const allChars = [
    ...(shotCharacters || []),
    ...(projectCharacters || []).filter(c => c.category === 'narrator')
  ];

  // 1. Index admin timing + admin text by characterId
  const adminTimingByChar = {};
  const adminTextByChar = {};
  const preProdDialogues = Array.isArray(shot.preProduction?.dialogues) ? shot.preProduction.dialogues : [];
  for (const ad of preProdDialogues) {
    if (!ad?.characterId) continue;
    const start = parseSeconds(ad.startTime ?? ad.start_time);
    const end = parseSeconds(ad.endTime ?? ad.end_time);
    if (
      !adminTimingByChar[ad.characterId] ||
      (start !== null && start < (adminTimingByChar[ad.characterId].startTime ?? Infinity))
    ) {
      adminTimingByChar[ad.characterId] = { startTime: start, endTime: end };
    }
    if (!adminTextByChar[ad.characterId] && ad.text) {
      adminTextByChar[ad.characterId] = ad.text;
    }
  }

  // 2. Group user-authored shot.dialogues per character (concatenate multi-line)
  const sortedUser = [...(shot.dialogues || [])].sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
    if (a.index !== undefined && b.index !== undefined) return a.index - b.index;
    const aTime = parseFloat(a.startTime || a.start_time || '0');
    const bTime = parseFloat(b.startTime || b.start_time || '0');
    return aTime - bTime;
  });
  const charMap = {};
  sortedUser.forEach((d, idx) => {
    const cid = d.characterId;
    if (!cid) return;
    if (!charMap[cid]) {
      charMap[cid] = {
        texts: [],
        order: d.order ?? idx,
        characterName: d.characterName,
        // Feb 2026 — carry the pre-generated GCS audio URL from
        // shot.dialogues[].audioUrl (populated by the pre-prod modal
        // when a character voice was assigned).
        audioUrl: d.audioUrl || d.audio_url || null,
        voiceId:  d.voiceId  || d.voice_id  || null,
      };
    }
    if (d.text) charMap[cid].texts.push(d.text);
    // First non-empty audioUrl wins for a given character (matches text concat).
    if (!charMap[cid].audioUrl && (d.audioUrl || d.audio_url)) {
      charMap[cid].audioUrl = d.audioUrl || d.audio_url;
    }
    if (!charMap[cid].voiceId && (d.voiceId || d.voice_id)) {
      charMap[cid].voiceId = d.voiceId || d.voice_id;
    }
  });

  // 3. Build dialogue entries per character
  const initial = {};
  let cursor = 0;
  Object.entries(charMap).forEach(([cid, data]) => {
    const ch = allChars.find(c => c.id === cid);
    if (!ch) return;
    const userText = data.texts.join(' ').trim();
    const text = userText || adminTextByChar[cid] || '';
    if (!text) return;
    const dur = estimateSpeechDuration(text);
    const adminTiming = adminTimingByChar[cid];
    let start, end;
    if (adminTiming && adminTiming.startTime !== null && adminTiming.startTime !== undefined) {
      start = adminTiming.startTime;
      end = (adminTiming.endTime !== null && adminTiming.endTime !== undefined)
        ? adminTiming.endTime
        : start + dur;
    } else {
      start = cursor;
      end = cursor + dur;
      cursor = end;
    }
    initial[cid] = {
      startTimecode: formatSec(start),
      endTimecode: formatSec(end),
      text,
      order: data.order,
      audioUrl: data.audioUrl || null,
      voiceId:  data.voiceId  || null,
    };
  });

  // 4. Surface admin-only dialogues (admin has timing+text, user didn't author)
  Object.entries(adminTimingByChar).forEach(([cid, t]) => {
    if (initial[cid]) return;
    const ch = allChars.find(c => c.id === cid);
    if (!ch) return;
    const text = (adminTextByChar[cid] || '').trim();
    if (!text) return;
    const dur = estimateSpeechDuration(text);
    const start = t.startTime ?? 0;
    const end = t.endTime ?? (start + dur);
    initial[cid] = {
      startTimecode: formatSec(start),
      endTimecode: formatSec(end),
      text,
      order: 500
    };
  });

  // 5. Narrator (uses shot.narration as text source)
  const narrator = (projectCharacters || []).find(c => c.category === 'narrator');
  if (narrator && !initial[narrator.id]) {
    const text = shot.narration || '';
    if (text) {
      const dur = estimateSpeechDuration(text) || 3;
      initial[narrator.id] = {
        startTimecode: formatSec(cursor),
        endTimecode: formatSec(cursor + dur),
        text,
        order: 999
      };
    }
  }

  return initial;
};

/**
 * Build the timed actions array — same shape modal stores in `timedActions`
 * state. Flattens preProduction.character_actions[].timeline[].
 */
export const buildTimedActions = (shot, totalDurationGuess) => {
  const preProd = shot.preProduction || {};
  const rawActions = preProd.character_actions || preProd.actions || preProd.video_prompt?.actions || [];
  const flattened = [];

  if (Array.isArray(rawActions) && rawActions.length > 0) {
    if (rawActions[0]?.timeline || rawActions[0]?.character_name) {
      rawActions.forEach((charAction) => {
        const charName = charAction.character_name || charAction.characterName || 'Character';
        (charAction.timeline || []).forEach((entry) => {
          let startSec = 0;
          const tc = entry.timecode || entry.time || '0';
          if (String(tc).includes(':')) {
            const [mm, ss] = String(tc).split(':');
            startSec = (parseInt(mm) || 0) * 60 + (parseFloat(ss) || 0);
          } else {
            startSec = parseFloat(tc) || 0;
          }
          const durMs = entry.duration_ms || 2000;
          const endSec = startSec + durMs / 1000;
          const text = entry.action || entry.description || '';
          flattened.push({
            startTime: formatSec(startSec),
            endTime: formatSec(endSec),
            action: `${charName}: ${text}${entry.emotion ? ` [${entry.emotion}]` : ''}`,
            character: charName,
            emotion: entry.emotion || null
          });
        });
      });
    } else {
      const total = Math.max(1, totalDurationGuess || rawActions.length);
      rawActions.forEach((a, idx) => {
        const start = a.start_time ?? a.startTime ?? a.start;
        const end = a.end_time ?? a.endTime ?? a.end;
        flattened.push({
          startTime: start !== undefined && start !== null ? formatSec(parseSeconds(start) ?? 0) : formatSec(idx * (total / rawActions.length)),
          endTime: end !== undefined && end !== null ? formatSec(parseSeconds(end) ?? 0) : formatSec((idx + 1) * (total / rawActions.length)),
          action: a.action || a.description || a.text || '',
          character: a.character || null,
          emotion: a.emotion || null
        });
      });
    }
  }

  // Fallback to main_prompt as a single block when no actions present
  if (flattened.length === 0) {
    const main = preProd.cinematicPrompt || preProd.cinematic_prompt
      || preProd.video_prompt?.main_prompt || preProd.video_prompt?.prompt
      || preProd.main_prompt || preProd.prompt
      || shot.description || shot.startFramePrompt || '';
    if (main) {
      flattened.push({
        startTime: '0.0s',
        endTime: formatSec(totalDurationGuess || 8),
        action: main,
        character: null,
        emotion: null
      });
    }
  }

  return flattened;
};

/**
 * Compute content-driven duration the same way the modal does:
 *   max(8, ceil(maxDialogueEnd), narrationEstimate)
 */
export const computeContentDuration = (dialoguesMap, shot) => {
  const maxDialogueEnd = Object.values(dialoguesMap || {}).reduce((max, d) => {
    const end = parseFloat(d.endTimecode) || 0;
    return Math.max(max, end);
  }, 0);
  const narrationDuration = estimateSpeechDuration(shot.narration || '');
  return Math.max(8, Math.ceil(maxDialogueEnd), narrationDuration);
};

/**
 * Construct the final prompt — IDENTICAL to VideoPreProductionModal.constructFinalPrompt.
 *
 * ASMR mode: when `audioStyle === 'asmr'`, every speech/music/effect section
 * is stripped and replaced with a single [ASMR] tag. This forces the video
 * model to render ONLY ASMR audio — no dialogue, narration, SFX, VFX, BGM,
 * or ambient. The ambient field (if populated) is reused as the ASMR cue
 * because it's the closest semantic match; otherwise a sensible default is
 * emitted so the model always gets explicit ASMR direction.
 */
export const constructFinalPrompt = ({
  cameraShot,
  timedActions,
  dialoguesMap,
  sfx, vfx, bgm, ambient,
  textOverlays,
  projectCharacters,
  narrator,
  audioStyle = 'both'
}) => {
  const cinematicActions = (timedActions || [])
    .filter(a => a.action?.trim())
    .map(a => `@[${a.startTime}-${a.endTime}] ${a.action.trim()}`)
    .join(' ');

  let finalPrompt = `[${cameraShot || ''}] ${cinematicActions}`.trim();

  if (audioStyle === 'asmr') {
    // Strip all other audio; replace with a single ASMR directive.
    const asmrCue = (ambient && ambient.trim())
      || 'Close-mic ASMR: crisp, intimate textures that match the on-screen action. No voices, no music, no traditional SFX.';
    finalPrompt += ` [ASMR]: ${asmrCue}.`;
  } else {
    // Dialogue (non-narrator)
    const dialogueEntries = Object.entries(dialoguesMap || {})
      .filter(([charId]) => (projectCharacters || []).find(c => c.id === charId)?.category !== 'narrator')
      .filter(([, D]) => typeof D.text === 'string' && D.text.trim() !== '')
      .map(([charId, D]) => {
        const charName = (projectCharacters || []).find(c => c.id === charId)?.name || 'Character';
        return `@[${D.startTimecode}-${D.endTimecode}] ${charName}: "${D.text.trim()}"`;
      });
    if (dialogueEntries.length > 0) {
      finalPrompt += ` [Dialogue]: ${dialogueEntries.join(' ')}.`;
    }

    // Narration
    if (narrator && dialoguesMap?.[narrator.id]?.text?.trim()) {
      finalPrompt += ` [Narration]: "${dialoguesMap[narrator.id].text.trim()}".`;
    }

    if (sfx?.trim()) finalPrompt += ` [SFX]: ${sfx.trim()}.`;
    if (vfx?.trim()) finalPrompt += ` [VFX]: ${vfx.trim()}.`;
    if (bgm?.trim()) finalPrompt += ` [BGM]: ${bgm.trim()}.`;
    if (ambient?.trim()) finalPrompt += ` [Ambient]: ${ambient.trim()}.`;
  }

  const textOverlayEntries = (textOverlays || []).map(o => {
    if (!o.content?.trim()) return '';
    if (o.aiStyle) {
      return `Add text overlay: "${o.content}". Use a cinematic style that fits the scene.`;
    }
    return `Add text overlay: "${o.content}". Font: ${o.fontFamily}, Size: ${o.fontSize}px, Color: ${o.color}.`;
  }).filter(Boolean);
  if (textOverlayEntries.length > 0) {
    finalPrompt += ` [Text Overlays]: ${textOverlayEntries.join(' ')}`;
  }

  return finalPrompt.trim();
};

/**
 * Build the snake_case structured dialogues payload — IDENTICAL to modal.
 *
 * Feb 2026 — Narrator lines are ALSO included so their pre-generated TTS
 * `audio_url` reaches the video model as an audio reference (same shape as
 * character dialogue audio references). The `[Narration]: "…"` line in the
 * final prompt is kept for the model's textual context; this payload adds
 * the audible track alongside it.
 */
export const buildDialoguesPayload = (dialoguesMap, projectCharacters, shotCharacters) => {
  return Object.entries(dialoguesMap || {})
    .filter(([charId, D]) => {
      const ch = (projectCharacters || []).find(c => c.id === charId)
        || (shotCharacters || []).find(c => c.id === charId);
      const hasText = typeof D.text === 'string' && D.text.trim() !== '';
      return ch && hasText;
    })
    .map(([charId, D]) => {
      const ch = (projectCharacters || []).find(c => c.id === charId)
        || (shotCharacters || []).find(c => c.id === charId);
      return {
        character_id: charId,
        character_name: ch?.name || 'Character',
        start_time: D.startTimecode,
        end_time: D.endTimecode,
        text: D.text.trim(),
        order: D.order || 0,
        character_image: ch?.image || null,
        // Feb 2026 — voice payload for the video model.
        voice_id: D.voiceId || ch?.elevenLabsVoiceId || ch?.voiceId || null,
        audio_url: D.audioUrl || null,
      };
    })
    .sort((a, b) => (parseFloat(a.start_time) || 0) - (parseFloat(b.start_time) || 0));
};

/**
 * Build the snake_case cinematic_actions payload — IDENTICAL to modal.
 */
export const buildCinematicActionsPayload = (timedActions) => {
  return (timedActions || [])
    .filter(a => a.action?.trim())
    .map(a => ({
      start_time: a.startTime,
      end_time: a.endTime,
      action: a.action.trim(),
      character: a.character || null,
      emotion: a.emotion || null
    }));
};

/**
 * Build the list of BytePlus ModelArk (Seedance) actor assets attached to the
 * characters that appear in this shot. Each entry pairs a character with a
 * pre-approved admin-side asset (group_id + asset_id). The backend forwards
 * these to the Seedance video model so it reuses the pre-approved asset
 * instead of re-uploading reference imagery.
 *
 * Only emitted for characters that:
 *   • have both `byteplusAssetGroupId` and `byteplusAssetId` set, AND
 *   • have `useByteplusForSeedance !== false` (toggle in Actors Library)
 *
 * `generateVideo` only forwards the field when the resolved model is a
 * seedance variant — so including non-seedance characters here is a no-op.
 *
 * Returns [] when no eligible characters exist.
 */
const APPROVED_STATUS_RE = /^(approved|active|success|succeeded|ready|completed)$/i;

// Approved = both ids present, not opted out, and (if a status is known) an approved status.
export const isByteplusAssetApproved = (ch) => {
  if (!ch) return false;
  const groupId = ch.byteplusAssetGroupId || ch.byteplus_asset_group_id;
  const assetId = ch.byteplusAssetId || ch.byteplus_asset_id;
  if (!groupId || !assetId) return false;
  if (ch.useByteplusForSeedance === false) return false;
  const status = ch.byteplusAssetStatus || ch.byteplus_asset_status;
  return !status || APPROVED_STATUS_RE.test(String(status).trim());
};

// All photos of a character, in priority order, deduped.
export const getCharacterPhotoUrls = (ch) => {
  const urls = [ch?.image, ...(Array.isArray(ch?.images) ? ch.images : [])]
    .filter(u => typeof u === 'string' && u.length > 0);
  return [...new Set(urls)];
};

/**
 * byteplus_assets[]: one entry per SHOT character with an approved asset AND a
 * photo present in `referenceImages` (image_url = exact same URL string).
 * Project characters are only used to pick up backfilled asset ids.
 */
export const buildByteplusAssetsPayload = (projectCharacters, shotCharacters, referenceImages) => {
  const refs = new Set(Array.isArray(referenceImages) ? referenceImages : []);
  const projById = new Map((Array.isArray(projectCharacters) ? projectCharacters : []).filter(Boolean).map(c => [c.id, c]));
  const seen = new Set();
  const out = [];
  for (const sc of (Array.isArray(shotCharacters) ? shotCharacters : [])) {
    if (!sc || seen.has(sc.id)) continue;
    seen.add(sc.id);
    const ch = { ...(projById.get(sc.id) || {}), ...sc };
    if (!ch.byteplusAssetGroupId && projById.get(sc.id)?.byteplusAssetGroupId) ch.byteplusAssetGroupId = projById.get(sc.id).byteplusAssetGroupId;
    if (!ch.byteplusAssetId && projById.get(sc.id)?.byteplusAssetId) ch.byteplusAssetId = projById.get(sc.id).byteplusAssetId;
    if (!isByteplusAssetApproved(ch)) continue;
    const photos = getCharacterPhotoUrls(ch).filter(u => refs.has(u));
    if (photos.length === 0) continue;
    out.push({
      character_id: ch.id,
      character_name: ch.name || 'Character',
      asset_group_id: ch.byteplusAssetGroupId || ch.byteplus_asset_group_id,
      asset_id: ch.byteplusAssetId || ch.byteplus_asset_id,
      image_url: photos[0],
      ...(photos.length > 1 ? { image_urls: photos } : {}),
    });
  }
  return out;
};

// Advanced video edit: project-character picks → byteplus_assets (approved only, deduped per character).
export const buildAdvancedEditByteplusAssets = (picks, projectCharacters) => {
  const byId = new Map((Array.isArray(projectCharacters) ? projectCharacters : []).filter(Boolean).map(c => [c.id, c]));
  const out = new Map();
  for (const p of (Array.isArray(picks) ? picks : [])) {
    if (!p || p.kind !== 'character' || !p.url) continue;
    const ch = byId.get(p.characterId);
    if (!isByteplusAssetApproved(ch)) continue;
    const existing = out.get(ch.id);
    if (existing) {
      if (!existing.image_urls.includes(p.url)) existing.image_urls.push(p.url);
      continue;
    }
    out.set(ch.id, {
      character_id: ch.id,
      character_name: ch.name || 'Character',
      asset_group_id: ch.byteplusAssetGroupId || ch.byteplus_asset_group_id,
      asset_id: ch.byteplusAssetId || ch.byteplus_asset_id,
      image_url: p.url,
      image_urls: [p.url],
    });
  }
  return [...out.values()].map(({ image_urls, ...e }) => (image_urls.length > 1 ? { ...e, image_urls } : e));
};

// product_references[]: brand product sheets in the shot whose image is also in reference_images.
export const buildProductReferencesPayload = (shotElements, referenceImages) => {
  const refs = new Set(Array.isArray(referenceImages) ? referenceImages : []);
  const seen = new Set();
  const out = [];
  for (const el of (Array.isArray(shotElements) ? shotElements : [])) {
    if (!el || el.source !== 'brand-product-sheet') continue;
    const url = el.image;
    if (!url || !refs.has(url) || seen.has(url)) continue;
    seen.add(url);
    out.push({ name: el.name || 'Product', image_url: url });
  }
  return out;
};

/**
 * Build the ordered reference-image list for refs-to-video generation.
 *
 * Previous behaviour (Feb 2026, now superseded): sent `shot.panels[].image`
 * as `reference_images[]`. The panel images encoded the shot's keyframe
 * sequence verbatim, which caused Seedance refs-to-video to "lock" onto the
 * panel renders and ignore any natural motion between them.
 *
 * New behaviour (requested by product owner): send the VISUAL REFERENCES
 * (character portraits + element photographs) instead. The video model
 * now receives:
 *   • one image per character present in the shot (first-match wins:
 *     `character.image`), AND
 *   • one image per element present in the shot (first-match wins:
 *     `element.image`).
 *
 * This mirrors how refs-to-video models were originally designed to work —
 * reference imagery for identity, not keyframes for motion. Deduped on URL
 * so repeated characters/elements don't inflate the payload.
 */
export const buildCharacterElementReferenceImages = (shotCharacters, shotElements) => {
  const seen = new Set();
  const out = [];
  const push = (url) => {
    if (!url || typeof url !== 'string') return;
    if (seen.has(url)) return;
    seen.add(url);
    out.push(url);
  };
  for (const ch of (Array.isArray(shotCharacters) ? shotCharacters : [])) {
    push(ch?.image);
  }
  for (const el of (Array.isArray(shotElements) ? shotElements : [])) {
    push(el?.image);
  }
  return out;
};

/**
 * Build the per-character costume reference payload (Feb 2026).
 *
 * Each entry pairs a character present in the shot with their PRIMARY
 * costume image URL (first non-empty entry in `character.costumes` or
 * `character.costumeAndAccessoryImages`). The admin backend forwards
 * these as a secondary visual reference so refs-to-video models can
 * keep the character's outfit consistent across the generated shot.
 *
 * Only emitted for characters that:
 *   • are actually present in this shot, AND
 *   • carry at least one costume URL
 *
 * Payload shape (one entry per character; costume cannot be swapped
 * across characters by the backend):
 *   { character_id, character_name, costume_image_url }
 *
 * Returns [] when no character in the shot has a costume. Backward-
 * compatible: without costumes, generation falls back to character
 * portrait + element references only (previous behaviour unchanged).
 */
// Costume used for video: user's "Use in video" pick, else the most recently added one.
export const getCharacterCostumeForVideo = (ch) => {
  if (!ch) return null;
  const costumes = (Array.isArray(ch.costumes) ? ch.costumes : []).filter(Boolean);
  const legacy = (Array.isArray(ch.costumeAndAccessoryImages) ? ch.costumeAndAccessoryImages : []).filter(Boolean);
  if (ch.selectedCostumeUrl && (costumes.includes(ch.selectedCostumeUrl) || legacy.includes(ch.selectedCostumeUrl))) {
    return ch.selectedCostumeUrl;
  }
  if (costumes.length > 0) return costumes[costumes.length - 1];
  if (legacy.length > 0) return legacy[legacy.length - 1];
  return null;
};

// Replace any base64 costume URLs with hosted storage links before sending.
export const ensureHostedCostumeUrls = async (characterCostumes) => {
  if (!Array.isArray(characterCostumes) || characterCostumes.length === 0) return characterCostumes;
  const userEmail = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userEmail') : null;
  return Promise.all(characterCostumes.map(async (entry) => {
    const url = entry?.costume_image_url;
    if (typeof url !== 'string' || !url.startsWith('data:')) return entry;
    try {
      const hosted = await uploadDataUriToBackend(url, userEmail);
      return hosted ? { ...entry, costume_image_url: hosted } : entry;
    } catch (e) {
      console.warn(`⚠️ Costume upload failed for ${entry.character_name}, sending base64:`, e.message);
      return entry;
    }
  }));
};

export const AUDIO_REFERENCE_CATEGORIES = ['sfx', 'bgm', 'vfx', 'ambient'];

// Shot-level `{ sfx: {url,type,fileName,mimeType}, ... }` → request `audio_references[]`.
export const buildAudioReferencesPayload = (refsMap) => {
  if (!refsMap || typeof refsMap !== 'object') return [];
  return AUDIO_REFERENCE_CATEGORIES
    .map(category => {
      const ref = refsMap[category];
      if (!ref || typeof ref.url !== 'string' || !ref.url) return null;
      return {
        category,
        type: ref.type === 'video' ? 'video' : 'audio',
        url: ref.url,
        file_name: ref.fileName || null,
        mime_type: ref.mimeType || null,
      };
    })
    .filter(Boolean);
};

export const buildCharacterCostumesPayload = (shotCharacters) => {
  const out = [];
  const seen = new Set();
  for (const ch of (Array.isArray(shotCharacters) ? shotCharacters : [])) {
    if (!ch || !ch.id || seen.has(ch.id)) continue;
    seen.add(ch.id);
    const costumeUrl = getCharacterCostumeForVideo(ch);
    if (!costumeUrl) continue;
    out.push({
      character_id: ch.id,
      character_name: ch.name || 'Character',
      costume_image_url: costumeUrl,
    });
  }
  return out;
};

/**
 * One-call helper: returns everything `aiService.generateVideo()` needs for a
 * shot, using the SAME logic the modal would apply when auto-populating from
 * shot.preProduction. Used by the bulk-generation flow so it produces an
 * identical payload to clicking "Generate Video" individually.
 */
export const buildShotVideoPayload = (shot, projectData, shotCharacters, shotElements = null) => {
  const projectCharacters = projectData?.characters || [];
  const audioStyle = projectData?.audioStyle || 'both';
  // In ASMR mode, no dialogue/narration audio is generated — pass an empty
  // map to the prompt builder and the dialogues payload so no speech is
  // produced by the video model.
  const dialoguesMap = audioStyle === 'asmr'
    ? {}
    : buildDialoguesMap(shot, projectCharacters, shotCharacters);
  const duration = computeContentDuration(dialoguesMap, shot);
  const timedActions = buildTimedActions(shot, duration);

  const preProd = shot.preProduction || {};
  const cameraShot = preProd.camera?.movement || preProd.camera?.shot || preProd.cameraShot || '';

  // Audio cues — use modal-equivalent text fields.
  // Modal seeds these from preProd at modal-mount time; bulk replicates that.
  const audio = preProd.audio || {};
  let sfx = '';
  if (Array.isArray(audio.sfx) && audio.sfx.length > 0) {
    sfx = audio.sfx
      .map(s => {
        const snd = s.sound || s.description || s;
        const t = s.timecode ? ` @${s.timecode}` : '';
        return `${snd}${t}`;
      })
      .filter(Boolean)
      .join('; ');
  }
  let bgm = '';
  if (audio.bgm) {
    const txt = audio.bgm.description || audio.bgm.text || '';
    const mood = audio.bgm.mood ? ` (${audio.bgm.mood})` : '';
    const tempo = audio.bgm.tempo_bpm ? ` - ${audio.bgm.tempo_bpm} BPM` : '';
    bgm = (txt + mood + tempo).trim();
  }
  const ambient = audio.ambient?.description || '';
  let vfx = '';
  const vfxData = preProd.vfx || preProd.visual_effects;
  if (Array.isArray(vfxData) && vfxData.length > 0) {
    vfx = vfxData.map(v => v.effect || v.description || v).filter(Boolean).join('; ');
  } else if (typeof vfxData === 'string') {
    vfx = vfxData.trim();
  }

  const textOverlays = Array.isArray(shot.textOverlays) ? shot.textOverlays : [];

  const narrator = projectCharacters.find(c => c.category === 'narrator');

  const finalPrompt = constructFinalPrompt({
    cameraShot,
    timedActions,
    dialoguesMap,
    sfx, vfx, bgm, ambient,
    textOverlays,
    projectCharacters,
    narrator,
    audioStyle
  });

  const dialoguesData = buildDialoguesPayload(dialoguesMap, projectCharacters, shotCharacters);
  const cinematicActions = buildCinematicActionsPayload(timedActions);
  // Feb 2026 — per-character costume references (secondary visual reference
  // for refs-to-video models). Only emitted for shot characters that have
  // a costume URL on them. Falls back to [] when no character has a costume.
  const characterCostumes = buildCharacterCostumesPayload(shotCharacters);

  // Refs-to-video mode (Feb 2026, panel-less variant):
  //   • Original behaviour: `shot.panels[].image` presence toggled
  //     refs-to-video and supplied the keyframes.
  //   • Feb 2026 update #1: panels still toggled the mode, but the
  //     reference_images[] carried character portraits + element photos
  //     instead of panel renders.
  //   • Feb 2026 update #2 (THIS CHANGE): refs-to-video mode is now
  //     toggled by the PRESENCE OF ANY REFERENCE IMAGE — character or
  //     element — not panel presence. A shot with 0 panels but with
  //     characters/elements still generates a video (user requirement).
  //
  // Fallback cascade:
  //   1. If characters/elements supply refs → use them (preferred).
  //   2. Else if panels supply images → fall back to panel renders
  //      (preserves legacy projects that have panels but no characters).
  //   3. Else → non-refs mode: send start/end frame image if present.
  const panelImagesInOrder = Array.isArray(shot?.panels)
    ? shot.panels.map(p => p?.image).filter(Boolean)
    : [];
  const characterElementRefs = buildCharacterElementReferenceImages(
    shotCharacters,
    shotElements
  );
  const effectiveRefs = characterElementRefs.length > 0
    ? characterElementRefs
    : panelImagesInOrder;
  const isReferencesToVideo = effectiveRefs.length > 0;
  const byteplusAssets = buildByteplusAssetsPayload(projectCharacters, shotCharacters, isReferencesToVideo ? effectiveRefs : []);
  const productReferences = buildProductReferencesPayload(shotElements, isReferencesToVideo ? effectiveRefs : []);

  // Feb 2026 — capability tag sent alongside model_key so the admin backend
  // can route to the correct worker without having to parse the model slug.
  // Mirrors the admin catalog's capability vocabulary.
  const capability = isReferencesToVideo
    ? 'references-to-video'
    : (shot?.startFrameImage && shot?.endFrameImage
        ? 'start-end-frame'
        : (shot?.startFrameImage
            ? 'image-to-video'
            : 'text-to-video'));

  return {
    finalPrompt,
    dialoguesData,
    cinematicActions,
    byteplusAssets,
    productReferences,
    characterCostumes,
    audioReferences: buildAudioReferencesPayload(shot?.audioReferences),
    capability,
    duration,
    hasEndFrame: isReferencesToVideo
      ? (effectiveRefs.length > 1)
      : !!shot.endFrameImage,
    isReferencesToVideo,
    startFrameImage: isReferencesToVideo ? null : shot.startFrameImage,
    endFrameImage:   isReferencesToVideo ? null : (shot.endFrameImage || null),
    referenceImages: isReferencesToVideo ? effectiveRefs : null,
    aspectRatio: projectData?.aspectRatio || '16:9'
  };
};

// ─── Swap Objects & Characters (Video Editor) ───

// Swappable items for ONE shot: its characters, the costume each wears, and its props/elements.
export const buildSwapItems = (shot, projectCharacters) => {
  const all = (Array.isArray(projectCharacters) ? projectCharacters : []).filter(Boolean);
  const charRefs = [...(Array.isArray(shot?.characterIds) ? shot.characterIds : []), ...(Array.isArray(shot?.characters) ? shot.characters : [])];
  const elRefs = Array.isArray(shot?.elementIds) ? shot.elementIds : [];
  const inShot = (c, refs) => refs.includes(c.id) || (c.name && refs.includes(c.name));
  const people = all.filter(c => c.category !== 'element' && inShot(c, charRefs));
  const elements = all.filter(c => c.category === 'element' && (inShot(c, elRefs) || inShot(c, charRefs)));
  const items = [];
  for (const c of people) {
    const approved = isByteplusAssetApproved(c);
    items.push({
      key: `character:${c.id}`, type: 'character', name: c.name || 'Character', characterId: c.id,
      imageUrl: c.image || null,
      assetGroupId: approved ? (c.byteplusAssetGroupId || null) : null,
      assetId: approved ? (c.byteplusAssetId || null) : null,
    });
    const costume = getCharacterCostumeForVideo(c);
    if (costume) {
      items.push({ key: `costume:${c.id}`, type: 'costume', name: `${c.name || 'Character'}'s costume`, characterId: c.id, characterName: c.name || 'Character', imageUrl: costume });
    }
  }
  for (const e of elements) {
    items.push({ key: `element:${e.id}`, type: 'element', name: e.name || 'Element', elementId: e.id, imageUrl: e.image || null });
  }
  return items;
};

// Match a detected character to a Cast & World character (backend id first, then name).
export const matchDetectedCharacter = (det, projectCharacters) => {
  const people = (Array.isArray(projectCharacters) ? projectCharacters : []).filter(c => c && c.category !== 'element');
  if (det?.matchedCharacterId) {
    const byId = people.find(c => c.id === det.matchedCharacterId);
    if (byId) return byId;
  }
  const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  const n = norm(det?.wornByName || det?.name);
  if (!n) return null;
  return people.find(c => norm(c.name) === n)
    || people.find(c => { const cn = norm(c.name); return cn && (n.split(' ').includes(cn) || cn.split(' ').includes(n)); })
    || null;
};

// Detected-in-video objects → swap items (same shape as Cast & World items).
export const buildDetectedSwapItems = (detected, projectCharacters) => (Array.isArray(detected) ? detected : []).map(d => {
  const match = (d.type === 'character' || d.type === 'costume') ? matchDetectedCharacter(d, projectCharacters) : null;
  const approved = d.type === 'character' && isByteplusAssetApproved(match);
  return {
    key: `detected:${d.id}`,
    type: d.type,
    name: d.name,
    description: d.description || '',
    imageUrl: d.screenshotRemoved ? null : (d.imageUrl || null),
    screenshotRemoved: !!d.screenshotRemoved,
    timestampSec: d.timestampSec ?? null,
    bbox: d.bbox || null,
    detectedId: d.id,
    ...(match ? { characterId: match.id, matchedName: match.name } : {}),
    ...(d.type === 'costume' ? { characterName: match?.name || d.wornByName || d.name } : {}),
    assetGroupId: approved ? match.byteplusAssetGroupId : null,
    assetId: approved ? match.byteplusAssetId : null,
  };
});

const SWAP_PHRASE = {
  character: (it) => `the character ${it.name}`,
  costume: (it) => `the outfit/costume worn by ${it.characterName}`,
  element: (it) => `the object/element "${it.name}"`,
  prop: (it) => `the prop "${it.name}"`,
  location: (it) => `the location/background "${it.name}"`,
};
const SWAP_TARGET = { character: 'the person', costume: 'the outfit', location: 'the location' };
const describeItem = (it) => [
  it.description ? `— ${it.description}` : null,
  Number.isFinite(it.timestampSec) ? `(clearly visible at ${it.timestampSec}s)` : null,
].filter(Boolean).join(' ');

/**
 * items + { [key]: { url, name?, assetGroupId?, assetId?, useByteplusForSeedance? } }
 * → { referenceImages, prompt, swaps, byteplusAssets }. Only items with a replacement are touched.
 */
export const buildSwapRequest = (items, replacements, extraNote = '') => {
  const referenceImages = [];
  const refIndex = (url) => {
    if (!url) return null;
    let i = referenceImages.indexOf(url);
    if (i === -1) { referenceImages.push(url); i = referenceImages.length - 1; }
    return i + 1;
  };
  const lines = [];
  const swaps = [];
  const byteplusAssets = [];
  const touched = [];
  for (const it of (Array.isArray(items) ? items : [])) {
    const rep = replacements?.[it.key];
    if (!rep?.url) continue;
    const origN = refIndex(it.imageUrl);
    const repN = refIndex(rep.url);
    const target = SWAP_TARGET[it.type] || 'the object';
    const extra = describeItem(it);
    lines.push(`${lines.length + 1}. Replace ${SWAP_PHRASE[it.type](it)}${extra ? ` ${extra}` : ''}${origN ? ` (shown in reference image ${origN})` : ''} with ${target} in reference image ${repN}.`);
    touched.push(SWAP_PHRASE[it.type](it));
    const repHasAsset = it.type === 'character' && isByteplusAssetApproved({
      byteplusAssetGroupId: rep.assetGroupId, byteplusAssetId: rep.assetId,
      useByteplusForSeedance: rep.useByteplusForSeedance, byteplusAssetStatus: rep.assetStatus,
    });
    swaps.push({
      type: it.type,
      name: it.name,
      original_name: it.type === 'costume' ? it.characterName : it.name,
      ...(it.characterId ? { original_character_id: it.characterId } : {}),
      ...(it.elementId ? { original_element_id: it.elementId } : {}),
      original_image_url: it.imageUrl || null,
      ...(it.detectedId ? { detected_id: it.detectedId, original_description: it.description || null, timestamp_sec: it.timestampSec ?? null, bbox: it.bbox || null } : {}),
      ...(it.screenshotRemoved ? { use_description_only: true } : {}),
      replacement_image_url: rep.url,
      replacement_name: rep.name || null,
      ...(it.type === 'character' ? {
        original_asset_group_id: it.assetGroupId || null,
        original_asset_id: it.assetId || null,
        replacement_asset_group_id: repHasAsset ? rep.assetGroupId : null,
        replacement_asset_id: repHasAsset ? rep.assetId : null,
      } : {}),
    });
    if (repHasAsset) {
      byteplusAssets.push({
        character_id: it.characterId, character_name: it.name,
        asset_group_id: rep.assetGroupId, asset_id: rep.assetId, image_url: rep.url,
      });
    }
  }
  if (swaps.length === 0) return { referenceImages: [], prompt: '', swaps: [], byteplusAssets: [], keepUnchanged: [] };
  const keepUnchanged = (Array.isArray(items) ? items : [])
    .filter(it => !replacements?.[it.key]?.url)
    .map(it => ({ type: it.type, name: it.name, ...(it.description ? { description: it.description } : {}) }));
  const prompt = [
    'Swap ONLY the following, nothing else:',
    ...lines,
    keepUnchanged.length ? `Keep these EXACTLY unchanged: ${keepUnchanged.map(k => `${k.name}${k.description ? ` (${k.description})` : ''}`).join('; ')}.` : null,
    `Everything that is not listed above — every other person, face, outfit, object, prop, background, setting, camera angle and motion, lighting, colour grade, timing, actions, dialogue and lip movement — must remain EXACTLY as it is in the original video. Keep each swapped item in the same position, pose, motion and scale as the original ${touched.length > 1 ? 'items' : 'item'}.`,
    extraNote?.trim() ? `Additional note: ${extraNote.trim()}` : null,
  ].filter(Boolean).join('\n');
  return { referenceImages, prompt, swaps, byteplusAssets, keepUnchanged };
};
